// OutageTable.tsx
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Pencil, Search, Trash2 } from 'lucide-react';
import { getOutageRequisition, deleteOutageRequisition } from '../../../services/outageRequisitionSevice';
import Loader from '../../../components/Loader/Loader';
import OutageRequisitionModal from './OutageRequisitionModal';
import ConfirmModal from '../../../components/ConfirmModal/ConfirmModal';
import SuccessModal from '../../../components/ConfirmModal/SuccessModal';
import { useSelector } from 'react-redux';
import Pagination from '../../../components/Pagination/Pagination';
import { STORAGE_BASE_URL } from '../../../services/apiService';

const LOCKED_STATUSES = [
  'Outage Type Confirmed - Senior Planner',
  'Outage Type Confirmation Pending - Technical Support Officer',
  'Outage Type Confirmed - Technical Support Officer',
  'Approval Pending - Maintenance Manager',
  'Approved - Maintenance Manager',
  'Approval Pending - Technical Manager',
  'Approved - Technical Manager',
  'Approval Pending - Operation Manager',
  'Approved - Operation Manager',
  'Shutdown Pending',
  'Shutdown Confirmed',
];

const isLocked = (status: string) => LOCKED_STATUSES.includes(status);

type Outage = {
  id: string;
  refNo: string;
  createdDate: string;
  site: string;
  person: string;
  personAvatar: string | null;
  outageType: string;
  status: string;
};

type OutageRequisitionsProps = {
  onError?: (message: string) => void;
};

const OutageRequisitions: React.FC<OutageRequisitionsProps> = ({ onError }) => {
  const [outages, setOutages] = useState<Outage[]>([]);
  const [loading, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);

  // DELETE MODAL STATE
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // SUCCESS MODAL STATE
  const [successModal, setSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState({ title: '', message: '' });

  const [searchQuery, setSearchQuery] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // PAGINATION STATES
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const navigate = useNavigate();

  /**  USER ROLE (same pattern as TOI) */
  const role = useSelector((state: any) => state.user?.role);
  const isPlanner = role === 'planner';

  //  fetchData accepts page param and reads meta correctly
  const fetchData = async (page = 1, search = '') => {
    try {
      if (initialLoad) {
        setLoading(true);
      }
      const response = await getOutageRequisition(page, search);

      const items = response?.data || response || [];

      const mappedData: Outage[] = items.map((item: any) => ({
        id: item.id.toString(),
        refNo: item.reference_number.toString(),
        createdDate: item.created_at,
        site: item.site?.name || '-',
        person: item.user?.name || '-',
        personAvatar: item.user?.profile_photo ? `${STORAGE_BASE_URL}${item.user.profile_photo}` : null,
        outageType: item.outage_type?.title || '-',
        status: item.status || 'Pending',
      }));

      setOutages(mappedData);

      const meta = response?.meta || response?.data?.meta || null;
      setCurrentPage(meta?.current_page || page);
      setTotalPages(meta?.last_page || 1);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  };

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      if (currentPage === 1) {
        fetchData(1, searchQuery);
      } else {
        setCurrentPage(1);
      }
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchQuery]);

  useEffect(() => {
    fetchData(currentPage, searchQuery);
  }, [currentPage]);

  const handleDelete = (id: string) => {
    setDeleteId(id);
    setIsDeleteOpen(true);
  };

  const showSuccessMessage = (title: string, message: string) => {
    setSuccessMessage({ title, message });
    setSuccessModal(true);
  };

  if (loading && initialLoad) return <Loader />;

  return (
    <div>
      {/*  ADD BUTTON — PLANNER ONLY */}
      <div className="mt-6 mb-6 flex items-center gap-4">
        {isPlanner && (
          <button
            onClick={() => {
              setEditId(null);
              setShowModal(true);
            }}
            className="bg-red-600 text-white font-semibold px-4 py-2 rounded hover:bg-red-700 transition whitespace-nowrap"
          >
            + Outage Requisition
          </button>
        )}
        {/* Search */}
        <div className="flex items-center border rounded-md px-3 py-2 w-full md:w-80 ml-auto">
          <Search size={18} className="text-gray-400 mr-2" />
          <input
            type="text"
            placeholder="Search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full outline-none text-sm"
          />
        </div>
      </div>

      {/*  NO EARLY RETURN — show empty state inside table flow */}
      {!outages.length ? (
        <div className="text-center py-10">No data found</div>
      ) : (
        /* Table */
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm text-left border rounded shadow">
            <thead className="text-gray-500 text-xs">
              <tr className="h-[45px]">
                <th className="px-4 py-1 border text-xs font-medium text-gray-500">Sl. No</th>
                <th className="px-4 py-1 border text-xs font-medium text-gray-500">Ref No.</th>
                <th className="px-4 py-1 border text-xs font-medium text-gray-500">Created Date</th>
                <th className="px-4 py-1 border text-xs font-medium text-gray-500">Site</th>
                <th className="px-4 py-1 border text-xs font-medium text-gray-500">Name of Person Requiring Outage</th>
                <th className="px-4 py-1 border text-xs font-medium text-gray-500">Outage Type</th>
                <th className="px-4 py-1 border text-xs font-medium text-gray-500">Status</th>

                {/*  ACTION COLUMN — PLANNER ONLY */}
                {isPlanner && <th className="px-4 py-2 border text-center">Actions</th>}
              </tr>
            </thead>

            <tbody className="bg-white divide-y">
              {outages.map((item, index) => (
                <tr key={item.id} onClick={() => navigate(`/OutageRequisition/${item.id}`)} className="cursor-pointer hover:bg-gray-50">
                  <td className="px-4 py-2 text-gray-500 border text-center">{index + 1}</td>
                  <td className="px-4 py-2 border font-medium text-black-600">{item.refNo}</td>
                  <td className="px-4 py-2 border font-medium">
                    {item?.createdDate ? new Date(item.createdDate).toLocaleDateString('en-GB') : '-'}
                  </td>
                  <td className="px-4 py-2 border font-medium">{item.site}</td>

                  <td className="px-4 py-2 flex items-center gap-2 font-medium">
                    {/* <img src={item.personAvatar} alt={item.person} className="w-6 h-6 rounded-full" />
                    {item.person} */}
                    {item.personAvatar ? (
                      <img src={item.personAvatar} alt={item.person} className="w-6 h-6 rounded-full object-cover" />
                    ) : (
                      <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-xs font-medium text-gray-600">
                        {item.person?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                    )}
                    {item.person}
                  </td>

                  <td className="px-4 py-2 border font-medium">{item.outageType}</td>

                  <td className="px-4 py-2 flex items-center text-blue-600 font-medium">
                    <span className="w-2 h-2 bg-blue-600 rounded-full mr-2" />
                    {item.status}
                  </td>

                  {/*  ACTIONS — PLANNER ONLY */}
                  {/*  ACTIONS — PLANNER ONLY */}
                  {isPlanner && (
                    <td className="px-4 py-2 border text-center" onClick={(e) => e.stopPropagation()}>
                      <div className="flex justify-center gap-3">
                        <button
                          onClick={() => {
                            setEditId(item.id);
                            setShowModal(true);
                          }}
                          disabled={isLocked(item.status)}
                          className="text-blue-600 disabled:text-gray-300 disabled:cursor-not-allowed"
                          title={isLocked(item.status) ? 'Locked — cannot edit' : 'Edit'}
                        >
                          <Pencil size={16} />
                        </button>

                        {/* row delete button — disable while a delete is in flight OR when locked */}
                        <button
                          onClick={() => handleDelete(item.id)}
                          disabled={isDeleting || isLocked(item.status)}
                          className="text-red-600 hover:text-red-800 disabled:opacity-50 disabled:cursor-not-allowed disabled:text-gray-300 disabled:hover:text-gray-300"
                          title={isLocked(item.status) ? 'Locked — cannot delete' : 'Delete'}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/*  PAGINATION — outside overflow-x-auto, always visible */}
      <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />

      {/* MODAL */}
      {showModal && (
        <OutageRequisitionModal
          editId={editId}
          onClose={() => {
            setShowModal(false);
            setEditId(null);
          }}
          onSubmitSuccess={() => {
            fetchData(currentPage);
            showSuccessMessage(
              editId ? 'Outage Requisition Updated' : 'Outage Requisition Added',
              editId ? 'The Outage Requisition has been successfully updated.' : 'The Outage Requisition has been successfully added.',
            );
          }}
          onError={(message) => {
            onError?.(message);
          }}
        />
      )}

      {/* DELETE CONFIRMATION MODAL */}
      <ConfirmModal
        isOpen={isDeleteOpen}
        title="Are you sure you want to delete this Outage Requisition?"
        message=""
        confirmText="Yes"
        cancelText="Cancel"
        isLoading={isDeleting} // ← new: lets ConfirmModal show a spinner / disable its buttons
        onCancel={() => {
          if (isDeleting) return; // ← guard: don't allow cancel mid-delete
          setIsDeleteOpen(false);
          setDeleteId(null);
        }}
        onConfirm={async () => {
          if (deleteId !== null) {
            setIsDeleting(true);
            try {
              await deleteOutageRequisition(deleteId);
              fetchData(currentPage);
              showSuccessMessage('Outage Requisition Deleted', 'The Outage Requisition has been successfully deleted.');
            } catch (err: any) {
              console.error('Error deleting outage requisition:', err);
              const errorMessage = err?.response?.data?.message || err?.message || 'Failed to delete Outage Requisition';
              onError?.(errorMessage);
            } finally {
              setIsDeleting(false);
              setIsDeleteOpen(false);
              setDeleteId(null);
            }
          }
        }}
      />

      {/* SUCCESS MODAL */}
      <SuccessModal
        isOpen={successModal}
        onClose={() => setSuccessModal(false)}
        title={successMessage.title}
        message={successMessage.message}
        buttonText="OK"
        onButtonClick={() => setSuccessModal(false)}
      />
    </div>
  );
};

export default OutageRequisitions;
